(() => {
  let dispose = () => {};
  function initialize() {
    dispose();
    const config = document.querySelector('[data-absen-home-motion]');
    const main = document.getElementById('MainContent');
    if (!main || config?.dataset.enabled === 'false') return;
    const pageType = (main.dataset.template || '').split('.')[0];
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = matchMedia('(min-width: 750px)');
    const wrapper = document.querySelector('.page-wrapper');
    const root = matchMedia('(min-width: 990px)').matches ? wrapper : null;
    const animations = new Set();
    const events = new AbortController();
    let observer;
    let sceneObserver;
    let lastScroll = -Infinity;
    let deferredHover;
    let idleTimer;
    const entranceAnimations = new Map();

    function clearHover() { deferredHover = undefined; }
    function finishScroll() {
      const card = deferredHover;
      deferredHover = undefined;
      if (card?.isConnected && card.matches(':hover')) {
        card.dispatchEvent(new PointerEvent('pointerenter', { pointerType: 'mouse' }));
      }
    }
    function waitForIdle() {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(finishScroll, 190);
    }
    function onScroll() {
      lastScroll = performance.now();
      waitForIdle();
    }
    function rememberEntrance(animation, element) {
      animations.add(animation);
      entranceAnimations.set(animation, element);
      const cleanup = () => { animations.delete(animation); entranceAnimations.delete(animation); };
      animation.finished.then(cleanup, cleanup);
    }
    if (config?.dataset.steady === 'true') {
      const passive = { passive: true, signal: events.signal };
      wrapper?.addEventListener('scroll', onScroll, passive);
      window.addEventListener('scroll', onScroll, passive);
      window.addEventListener('wheel', event => {
        if (Math.abs(event.deltaY) >= Math.abs(event.deltaX)) onScroll();
      }, passive);
      window.addEventListener('touchmove', onScroll, passive);

      main.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !(event.target instanceof Element) ||
          !event.target.matches('.absen-parts__card, .absen-standard') ||
          performance.now() - lastScroll >= 190) return;
        deferredHover = event.target;
        event.stopImmediatePropagation();
        waitForIdle();
      }, { capture: true, signal: events.signal });
      main.addEventListener('pointerleave', event => {
        if (event.target === deferredHover) clearHover();
      }, { capture: true, signal: events.signal });
    }
    const directIntent = event => {
      clearHover();
      for (const [animation, element] of entranceAnimations) {
        if (element.contains(event.target)) animation.cancel();
      }
    };
    main.addEventListener('pointerdown', directIntent, { capture: true, signal: events.signal });
    main.addEventListener('keydown', directIntent, { capture: true, signal: events.signal });
    main.addEventListener('focusin', directIntent, { signal: events.signal });

    // Each scene has its own direction and sequence. Native carousel tracks and
    // the Parts / Why card transforms stay under their original controllers.
    const entrances = new WeakMap();
    let initializing = true;
    const sceneBounds = new Map();
    let initialViewport;
    if (config?.dataset.headings !== 'false' && !reduced.matches && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const options = entrances.get(entry.target);
          if (!entry.isIntersecting) continue;
          // Stop observing before moving the target; its own entrance must not
          // be mistaken for scrolling it out of view. Each one-shot effect releases on completion.
          observer.unobserve(entry.target);
          const elements = options.group ? [...entry.target.children] : [entry.target];
          elements.forEach((element, index) => {
            const x = desktop.matches ? options.x || '0px' : '0px';
            const y = desktop.matches ? options.distance ?? 80 : Math.min(options.distance ?? 28, 28) || (options.x ? 16 : 0);
            // Opaque image surfaces move inside their existing crop; they never
            // wait at low opacity during scrolling. Hero images are excluded.
            const frames = options.image ? [
              { translate: `0 ${desktop.matches ? '2.5%' : '1.5%'}`, scale: desktop.matches ? 1.08 : 1.04 },
              { translate: '0 0', scale: 1 }
            ] : [
              { translate: `${x} ${y}px` },
              { translate: '0 0' }
            ];
            if (options.group || options.fade) {
              frames[0].opacity = options.opacity ?? .25;
              frames[1].opacity = 1;
            }
            const animation = element.animate(frames, {
              duration: desktop.matches ? options.duration || 1000 : Math.min(options.duration || 650, 650),
              delay: (options.delay || 0) + Math.min(index, 4) * (options.stagger ?? (desktop.matches ? 110 : 65)),
              easing: 'cubic-bezier(.25,.1,.25,1)',
              fill: 'backwards'
            });
            rememberEntrance(animation, element);
          });
        }
      }, { root, rootMargin: '0px 0px 48px 0px', threshold: 0 });
      const pendingScenes = new Map();
      sceneObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          sceneObserver.unobserve(entry.target);
          pendingScenes.get(entry.target)?.forEach(element => observer.observe(element));
          pendingScenes.delete(entry.target);
        }
      }, { root, rootMargin: '200px 0px', threshold: 0 });
      const watch = (element, options) => {
        if (entrances.has(element)) return;
        entrances.set(element, options);
        const viewport = initializing && initialViewport
          ? initialViewport
          : root ? root.getBoundingClientRect() : { top: 0, bottom: innerHeight };
        if (initializing) initialViewport = viewport;
        const scene = element.closest('.shopify-section');
        if (initializing && scene && !sceneBounds.has(scene)) sceneBounds.set(scene, scene.getBoundingClientRect());
        const sceneBox = initializing && sceneBounds.get(scene);
        // Observe the scene first: observing/measuring its descendants would
        // force layout inside content-visibility's skipped subtree.
        if (sceneBox && sceneBox.top >= viewport.bottom) {
          if (!pendingScenes.has(scene)) {
            pendingScenes.set(scene, []);
            sceneObserver.observe(scene);
          }
          pendingScenes.get(scene).push(element);
          return;
        }
        const bounds = element.getBoundingClientRect();
        // A late module must not rewind content that has already been painted.
        // Below-fold targets still enter from just outside the visible viewport.
        if (initializing && bounds.height && bounds.bottom > viewport.top && bounds.top < viewport.bottom) return;
        observer.observe(element);
      };
      const each = (selector, options) => main.querySelectorAll(selector).forEach((element, index) => {
        watch(element, typeof options === 'function' ? options(element, index) : { ...options });
      });
      if (pageType === 'index') {
        each('.absen-home-display-overview__header, .absen-parts__header, .absen-featured-hero__header, .absen-standards__heading, .ah2-cases__heading, .ah2-logo-list__header', { group: true, distance: 80 });
        each('.absen-home-display-overview__card', (_, index) => ({ x: index ? '50px' : '-50px', distance: 70, delay: index * 120 }));

        // Service and LED browsing get visible vertical entry without sideways tiles.
        each('.absen-home-service-overview__header, .ah2-projects__heading', { group: true, distance: 32, duration: 650, stagger: 75, opacity: .55 });
        each('.absen-home-service-overview__card', (_, index) => ({ distance: 28, duration: 650, delay: Math.min(index, 4) * 75 }));
        each('.ah2-project-card__media img', (_, index) => ({ distance: 32, duration: 700, delay: Math.min(index, 4) * 70 }));
        each('.ah2-project-card__copy', { distance: 0, duration: 500, fade: true, opacity: .6 });
        each('.ah2-logo-list__item', (_, index) => ({ distance: 6, duration: 280, delay: index * 50, fade: true, opacity: .85 }));
        each('.ah2-case', element => ({ x: '35px', distance: 80, delay: Math.min([...element.parentElement.children].indexOf(element) % 6, 4) * (desktop.matches ? 110 : 60) }));

        // Approved homepage scenes retain their distinct presentation motion.
        each('.absen-parts__row', (_, index) => ({ x: index ? '100px' : '-100px', distance: 50, delay: index * 160 }));
        each('.absen-home-energy__content', { x: '-100%', distance: 0, delay: 100, duration: 800, fade: true });
        each('.absen-home-energy__media', { x: '100%', distance: 0, delay: 200, duration: 800 });
        each('.absen-featured-hero__copy', { x: '-100%', distance: 0, delay: 100, duration: 800, fade: true });
        each('.absen-featured-hero__visual', { x: '40%', distance: 0, delay: 200, duration: 800 });
        each('.absen-standards__aside', { x: '80px', distance: 0, delay: 100, fade: true });
        each('.absen-standards__grid', { x: '50px', distance: 60, delay: 160 });
        each('.absen-home-display-overview__media img, .absen-home-energy__image, .absen-featured-hero__image, .ah2-case__media img', { image: true, delay: 160, duration: 1100 });
      } else if (pageType === 'collection' || pageType === 'search') {
        // Reveal only artwork inside its native crop. Names, prices, card frames
        // and filtering controls remain stationary and immediately readable.
        each('.absen-collection-banner__copy', { distance: 24, duration: 600, fade: true, opacity: .55 });
        const registerResultImage = (image, index) => {
          if (image.closest('slideshow-slide[hidden]:not([reveal])')) return;
          watch(image, { distance: 24, duration: 620, fade: true, opacity: .7, delay: index % 4 * 70 });
        };
        const registerResults = () => main.querySelectorAll('.product-grid__item').forEach((card, index) => {
          card.querySelectorAll('.card-gallery .product-media__image').forEach(image => registerResultImage(image, index));
        });
        registerResults();
        main.addEventListener('absen:results:append', registerResults, { signal: events.signal });
        main.addEventListener('slideshow:select', event => {
          const slide = event.detail?.slide;
          if (!(slide instanceof HTMLElement)) return;
          const card = slide.closest('.product-grid__item');
          if (!card) return;
          const index = [...card.parentElement.children].indexOf(card);
          slide.querySelectorAll('.product-media__image').forEach(image => registerResultImage(image, index));
        }, { signal: events.signal });
        document.addEventListener(pageType === 'collection' ? 'shopify:collection:update' : 'shopify:search:update', event => {
          event.promise.then(() => {
            if (!events.signal.aborted) registerResults();
          }, () => {});
        }, { signal: events.signal });
      } else if (pageType === 'product') {
        // The purchase area stays readable and usable immediately; no image zoom
        // or transforms are applied to variant, price, cart or sticky controls.
        each('.product-information__media', { distance: 10, duration: 450 });
        each('.product-details h1', { distance: 10, duration: 400, fade: true, opacity: .7 });

        // Narrative content uses restrained reveal and paired image/copy motion.
        each('.absen-vx__section > .absen-vx__wrap > .absen-vx__eyebrow, .absen-vx__section > .absen-vx__wrap > h2, .absen-vx__section > .absen-vx__wrap > .absen-vx__lead, .absen-vx__unified-heading', { distance: 18, duration: 520, fade: true, opacity: .65 });
        each('.absen-vx__stage', { distance: 28, duration: 700 });
        each('.absen-vx__stage-copy', { x: '-28px', distance: 0, duration: 650, delay: 70, fade: true, opacity: .65 });
        each('.absen-vx__stage .family-art', { x: '28px', distance: 0, duration: 700, delay: 100 });
        each('.absen-vx__tile', (_, index) => ({ distance: 18, duration: 500, delay: index % 4 * 55 }));
        each('.absen-vx__system-copy', { x: '-28px', distance: 0, duration: 650, fade: true, opacity: .65 });
        each('.absen-vx__system-visual', { x: '28px', distance: 0, duration: 700, delay: 80 });
        each('.absen-vx__doc', (_, index) => ({ distance: 10, duration: 350, delay: index % 3 * 35 }));
        each('.absen-vx__faq > div:first-child, .absen-vx__cta-inner > .cta-content', { distance: 18, duration: 500, fade: true, opacity: .7 });
        each('.absen-vx__cta-product', { x: '24px', distance: 0, duration: 650 });
        each('.absen-product-detail-tabs__rte, .absen-product-details-panel__description', { distance: 12, duration: 450, fade: true, opacity: .75 });
        each('.absen-product-detail-tabs__image-button', { distance: 14, duration: 600 });
      } else if (['page', 'blog', 'article'].includes(pageType)) {
        // Service landing: content, imagery and scene frames have their own
        // cadence; lifecycle/scenario tracks and tab controllers remain native.
        each('.absen-services-hero__content', { group: true, distance: 32, duration: 750, stagger: 85, opacity: .4 });
        each('.absen-services-trust__item', (_, index) => ({ distance: 22, duration: 600, delay: index * 65 }));
        each('.absen-services-heading > div:first-child', { group: true, distance: 30, duration: 650, stagger: 70, opacity: .55 });
        each('.absen-services-finder__card, .absen-services-products__card', (_, index) => ({ distance: 28, duration: 650, delay: index % 6 * 65 }));
        each('.absen-services-capabilities__image, .absen-services-lifecycle__display, .absen-services-scenarios__preview', { distance: 28, duration: 750 });
        each('.absen-services-scenarios__card', (_, index) => ({ distance: 20, duration: 600, delay: index * 55 }));
        each('.absen-services-testimonials__story-copy, .absen-services-value__copy', { x: '-32px', distance: 0, duration: 700, fade: true, opacity: .55 });
        each('.absen-services-testimonials__media, .absen-services-value__visual', { x: '32px', distance: 0, duration: 750, delay: 100 });
        each('.absen-services-contact__inner', { distance: 26, duration: 650 });

        // Other editorial routes: reveal headings and pictures, never forms,
        // maps, FAQ rows, cart/account/checkout or policy text.
        each('.absen-page-header__copy, .absen-about-hero > div, .absen-about-intro > div', { group: true, distance: 30, duration: 700, stagger: 70, opacity: .5 });
        each('.absen-about-story__media, .absen-about-story__copy', (_, index) => ({ x: index % 2 ? '32px' : '-32px', distance: 0, duration: 750 }));
        each('.absen-about-values header, .absen-about-team header, .absen-about-timeline header, .absen-contact-questions header, .absen-faq-promises header', { group: true, distance: 26, duration: 650, opacity: .55 });
        each('.absen-about-values__grid > article, .absen-about-team__grid > article, .absen-faq-promises__grid > article', (_, index) => ({ distance: 24, duration: 650, delay: index % 4 * 65 }));
        each('.absen-about-timeline__item', { distance: 30, duration: 700 });
        each('.absen-faq-content__group > h2, .absen-faq-content aside > h2, .absen-faq-content aside > img', { distance: 24, duration: 650 });
        each('.absen-blog-featured__main, .absen-blog-featured__list > li, .absen-blog-grid__cards > article > a', (_, index) => ({ distance: 28, duration: 650, delay: index % 4 * 65 }));
        each('.absen-contact-map-form__grid > div:first-child > h2, .absen-contact-map-form__grid > div:first-child > p, .absen-about-newsletter > h2, .absen-about-newsletter > p', { distance: 24, duration: 650, fade: true, opacity: .6 });
        each('.absen-blog-grid__header', { distance: 24, duration: 650, fade: true, opacity: .6 });
        if (pageType === 'article') each('h1', { distance: 24, duration: 650, fade: true, opacity: .6 });
      }

    }

    document.documentElement.classList.add('absen-motion-ready');
    main.classList.add('absen-home-native-motion');
    initializing = false;
    const pressSelector = '.button, .ah2-hero__button, .absen-home-energy__quote, .absen-featured-hero__buy-link, .absen-quote-card__button';
    document.addEventListener('pointerdown', event => {
      if (reduced.matches || event.button !== 0) return;
      const button = event.target.closest(pressSelector);
      if (!button || button.matches(':disabled') || button.closest('.absen-parts, .absen-standards')) return;
      const animation = button.animate([{ scale: 1 }, { scale: .975 }, { scale: 1 }], { duration: 220, easing: 'cubic-bezier(.22,1,.36,1)' });
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
    }, { signal: events.signal });
    reduced.addEventListener('change', () => {
      if (reduced.matches) {
        observer?.disconnect();
        sceneObserver?.disconnect();
        animations.forEach(animation => animation.cancel());
      }
    }, { signal: events.signal });
    dispose = () => {
      clearHover();
      clearTimeout(idleTimer);
      events.abort();
      observer?.disconnect();
      sceneObserver?.disconnect();
      animations.forEach(animation => animation.cancel());
      main.classList.remove('absen-home-native-motion');
      document.documentElement.classList.remove('absen-motion-ready');
    };
  }
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.querySelector('[data-absen-home-motion]')) dispose();
  });
})();
